<script>
import { defineAsyncComponent, useTemplateRef } from 'vue';
import { DirectUpload } from 'dashboard/helper/multipartUpload';
import { useWindowSize } from '@vueuse/core';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useTrack } from 'dashboard/composables';
import keyboardEventListenerMixins from 'shared/mixins/keyboardEventListenerMixins';

import ReplyToMessage from './ReplyToMessage.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel.vue';
import ReplyEmailHead from './ReplyEmailHead.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
import CompactReplyComposer from 'dashboard/components/widgets/WootWriter/CompactReplyComposer.vue';
import Modal from 'dashboard/components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import LabelDropdown from 'shared/components/ui/label/LabelDropdown.vue';
import CopilotReplyBottomPanel from 'dashboard/components/widgets/WootWriter/CopilotReplyBottomPanel.vue';
import ArticleSearchPopover from 'dashboard/routes/dashboard/helpcenter/components/ArticleSearch/SearchPopover.vue';
import CopilotEditorSection from './CopilotEditorSection.vue';
import MessageSignatureMissingAlert from './MessageSignatureMissingAlert.vue';
import ReplyBoxBanner from './ReplyBoxBanner.vue';
import QuotedEmailPreview from './QuotedEmailPreview.vue';
import StickerPickerDialog from 'dashboard/components-next/whatsapp/StickerPickerDialog.vue';
import AttachedContactsPreview from 'dashboard/components-next/Conversation/AttachedContactsPreview.vue';
import ContactAttachmentModal from 'dashboard/components-next/Conversation/ContactAttachmentModal.vue';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import ScheduledMessageSequenceEditor from 'dashboard/routes/dashboard/conversation/components/ScheduledMessageSequenceEditor.vue';
import {
  getDirectUploadUrl,
  setDirectUploadAuthHeaders,
} from 'dashboard/helper/directUploadsHelper';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { CMD_AI_ASSIST } from 'dashboard/helper/commandbar/events';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
} from '@chatwoot/utils';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import ContentTemplates from './ContentTemplates/ContentTemplatesModal.vue';
import conversationApi from 'dashboard/api/inbox/conversation';
import scheduledMessagesApi from 'dashboard/api/scheduledMessages';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import {
  trimContent,
  debounce,
  getRecipients,
  getAllowedFileTypesByChannel,
} from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import {
  extractQuotedEmailText,
  buildQuotedEmailHeader,
  truncatePreviewText,
  appendQuotedTextToMessage,
} from 'dashboard/helper/quotedEmailHelper';
import {
  CONVERSATION_EVENTS,
  CAPTAIN_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import mediaEditorMixin from 'dashboard/mixins/mediaEditorMixin';
import {
  appendSignature,
  removeSignature,
  getEffectiveChannelType,
  getAgentVariables,
  getContactVariables,
} from 'dashboard/helper/editorHelper';
import { useCopilotReply } from 'dashboard/composables/useCopilotReply';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import {
  checkFileSizeLimit,
  isFileTypeAllowedForChannel,
} from 'shared/helpers/FileHelper';
import {
  hasPixPaymentConfiguration,
  pixPaymentDisplayType,
} from 'dashboard/helper/pixPaymentHelper';

import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';
import { attendanceDraftKey } from 'dashboard/helper/webAttendance';

const GROUP_CONTACT_MENTION_REGEX =
  /\[@([^\]]+)\]\(mention:\/\/group[_-]contact\/(\d+)\/([^)]+)\)|mention:\/\/group[_-]contact\/(\d+)\/([^\s)]+)/g;

const EmojiIconPicker = defineAsyncComponent(
  () =>
    import('dashboard/components-next/emoji-icon-picker/EmojiIconPicker.vue')
);

export default {
  components: {
    ArticleSearchPopover,
    AttachmentPreview,
    AttachedContactsPreview,
    AudioRecorder,
    ReplyBoxBanner,
    EmojiIconPicker,
    MessageSignatureMissingAlert,
    Modal,
    NextButton,
    LabelDropdown,
    ReplyBottomPanel,
    ReplyEmailHead,
    ReplyToMessage,
    ReplyTopPanel,
    ContentTemplates,
    ContactAttachmentModal,
    WhatsappTemplates,
    WootMessageEditor,
    QuotedEmailPreview,
    ScheduledMessageSequenceEditor,
    StickerPickerDialog,
    CopilotEditorSection,
    CopilotReplyBottomPanel,
    CompactReplyComposer,
    MediaEditor: defineAsyncComponent(
      () => import('dashboard/components-next/Conversation/MediaEditor.vue')
    ),
  },
  mixins: [
    inboxMixin,
    fileUploadMixin,
    mediaEditorMixin,
    keyboardEventListenerMixins,
  ],
  props: {
    deliveryDestination: { type: String, default: 'channel' },
    isComposerActive: { type: Boolean, default: true },
    managedDestinations: { type: Boolean, default: false },
    initialReplyMode: { type: String, default: REPLY_EDITOR_MODES.REPLY },
    webAttendanceActive: { type: Boolean, default: false },
    popOutReplyBox: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:popOutReplyBox', 'toggleEditorSize', 'setReplyMode'],
  setup() {
    const {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
    } = useUISettings();

    const replyEditor = useTemplateRef('replyEditor');
    const messageEditor = useTemplateRef('messageEditor');
    const copilot = useCopilotReply();
    const { captainTasksEnabled } = useCaptain();
    const shortcutKey = useKbd(['$mod', '+', 'enter']);
    const { width: windowWidth } = useWindowSize();

    return {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
      replyEditor,
      messageEditor,
      copilot,
      captainTasksEnabled,
      shortcutKey,
      windowWidth,
    };
  },
  data() {
    return {
      message: '',
      inReplyTo: {},
      isFocused: false,
      showEmojiPicker: false,
      attachedFiles: [],
      attachedContacts: [],
      isRecordingAudio: false,
      recordingAudioState: '',
      recordingAudioDurationText: '',
      replyType: this.managedDestinations
        ? this.initialReplyMode
        : REPLY_EDITOR_MODES.REPLY,
      bccEmails: '',
      ccEmails: '',
      toEmails: '',
      doAutoSaveDraft: () => {},
      showWhatsAppTemplatesModal: false,
      showContentTemplatesModal: false,
      showContactAttachmentModal: false,
      showStickerPicker: false,
      showScheduleModal: false,
      scheduledAt: '',
      scheduledLabelId: '',
      scheduledReason: '',
      scheduledSenderId: '',
      showScheduleLabelDropdown: false,
      scheduledItems: [],
      scheduleUploadCount: 0,
      scheduleCopy: {
        dateTime: 'Data e hora',
        label: 'Etiqueta',
        agent: 'Operador',
        reason: 'Motivo',
        optional: '(opcional)',
      },
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showMentions: false,
      showUserMentions: false,
      showGroupMentions: false,
      showCannedMenu: false,
      showVariablesMenu: false,
      newConversationModalActive: false,
      showArticleSearchPopover: false,
      hasRecordedAudio: false,
      recordedAudioViewOnce: false,
      isRecordedAudioUploadPending: false,
      sendRecordedAudioAfterUpload: false,
      copilotAcceptedMessages: {},
      groupMentionContacts: [],
      isLoadingGroupMentionContacts: false,
      groupMentionFetchTimeout: null,
    };
  },
  computed: {
    isWebDestination() {
      return this.deliveryDestination === 'web';
    },
    ...mapGetters({
      currentChat: 'getSelectedChat',
      messageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      lastEmail: 'getLastEmailInSelectedChat',
      accountLabels: 'labels/getLabels',
      getAccount: 'accounts/getAccount',
    }),
    shouldFocusMessageEditorOnMount() {
      return this.windowWidth >= wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    currentContact() {
      const senderId = this.currentChat?.meta?.sender?.id;
      if (!senderId) return {};
      return this.$store.getters['contacts/getContact'](senderId);
    },
    isAdministrator() {
      const account = this.currentUser.accounts?.find(
        item => item.id === this.currentChat.account_id
      );
      return account?.role === 'administrator';
    },
    showPixPaymentButton() {
      const config = this.inbox?.provider_config || {};

      return (
        !this.isWebDestination &&
        this.isAUnoapiChannel &&
        !this.isOnPrivateNote &&
        !this.currentChat?.group &&
        hasPixPaymentConfiguration(config)
      );
    },
    pixPaymentMessageContent() {
      const config = this.inbox?.provider_config || {};

      return this.$t('CONVERSATION.REPLYBOX.PIX_PAYMENT.MESSAGE_CONTENT', {
        type: pixPaymentDisplayType(config.pix_key_type),
        key: config.pix_key,
      });
    },
    scheduleAgents() {
      return this.$store.getters['agents/getAgents'] || [];
    },
    selectedScheduleLabel() {
      return this.accountLabels.find(
        label => label.id === Number(this.scheduledLabelId)
      );
    },
    scheduleAllowedFileTypes() {
      return getAllowedFileTypesByChannel({
        channelType: this.inbox?.channel_type,
        medium: this.inbox?.medium,
      });
    },
    isScheduleValid() {
      return (
        this.scheduledItems.length >= 1 &&
        this.scheduledItems.length <= 5 &&
        this.scheduledItems.every(
          item => item.content?.trim() || item.attachments?.length
        ) &&
        this.scheduleUploadCount === 0
      );
    },
    canUseGroupMentions() {
      return (
        !this.isWebDestination &&
        this.currentChat?.group &&
        this.isAUnoapiChannel &&
        !this.isOnPrivateNote
      );
    },
    shouldShowReplyToMessage() {
      return (
        this.inReplyTo?.id &&
        !this.isPrivate &&
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
        !this.is360DialogWhatsAppChannel &&
        !this.copilot.isActive.value
      );
    },
    showWhatsappTemplates() {
      // We support templates for API channels if someone updates templates manually via API
      // That's why we don't explicitly check for channel type here
      const templates = this.$store.getters['inboxes/getWhatsAppTemplates'](
        this.inboxId
      );
      return (
        !this.isWebDestination &&
        !!(templates && templates.length) &&
        !this.isPrivate &&
        !this.isAUnoapiChannel
      );
    },
    showContentTemplates() {
      return (
        !this.isWebDestination &&
        this.isATwilioWhatsAppChannel &&
        !this.isPrivate
      );
    },
    isPrivate() {
      if (
        this.currentChat.can_reply ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox
      ) {
        return this.isOnPrivateNote;
      }
      return true;
    },
    hasMeaningfulEditorContent() {
      const body = this.message || '';
      // Only strip the signature when it's actually being auto-appended.
      // If the toggle is off, the agent's text might happen to match their
      // saved signature and we'd incorrectly treat it as empty.
      const shouldStripSignature =
        !this.isPrivate && this.sendWithSignature && !!this.messageSignature;
      if (!shouldStripSignature) return !!body.trim();
      const stripped = removeSignature(
        body,
        this.messageSignature,
        getEffectiveChannelType(this.channelType, this.inbox?.medium || '')
      );
      return !!stripped.trim();
    },
    isReplyRestricted() {
      if (this.isWebDestination) return !this.webAttendanceActive;
      return (
        !this.currentChat?.can_reply &&
        !(this.isAWhatsAppChannel || this.isAPIInbox)
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    messagePlaceHolder() {
      if (this.isEditorDisabled) {
        if (this.isAWhatsAppChannel) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_WHATSAPP');
        }
        if (this.isAPIInbox) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_API');
        }
        return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED');
      }
      if (this.useCompactMessageComposer) {
        if (this.isPrivate) {
          return this.$t('CONVERSATION.REPLYBOX.COMPACT.PRIVATE_PLACEHOLDER');
        }
        if (
          !this.isWebDestination &&
          this.inbox?.channel_type === 'Channel::Whatsapp'
        ) {
          return this.$t(
            'CONVERSATION.REPLYBOX.COMPACT.PLACEHOLDER_WITH_SCHEDULE'
          );
        }
        return this.$t('CONVERSATION.REPLYBOX.COMPACT.PLACEHOLDER');
      }
      const placeholder = this.isPrivate
        ? this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT')
        : this.$t('CONVERSATION.FOOTER.MSG_INPUT');
      const shortcuts = [];
      if (
        !this.isWebDestination &&
        !this.isPrivate &&
        this.inbox?.channel_type === 'Channel::Whatsapp'
      ) {
        shortcuts.push(this.$t('CONVERSATION.FOOTER.SCHEDULE_SHORTCUT'));
      }
      if (this.captainTasksEnabled) {
        shortcuts.push(this.$t('CONVERSATION.FOOTER.CAPTAIN_SHORTCUTS'));
      }

      return [placeholder, ...shortcuts].join('\n');
    },
    composerAriaLabel() {
      return this.isPrivate
        ? this.$t('CONVERSATION.REPLYBOX.COMPACT.PRIVATE_PLACEHOLDER')
        : this.$t('CONVERSATION.REPLYBOX.COMPACT.PLACEHOLDER');
    },
    accountSettings() {
      return this.getAccount(this.currentChat?.account_id)?.settings || {};
    },
    useCompactMessageComposer() {
      if (this.managedDestinations) return true;
      return (
        !this.isAnEmailChannel &&
        this.accountSettings.use_legacy_message_composer !== true
      );
    },
    hasComposerContent() {
      return Boolean(
        this.hasMeaningfulEditorContent ||
          this.hasAttachments ||
          this.hasRecordedAudio ||
          this.hasAttachedContacts
      );
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    isReplyButtonDisabled() {
      if (this.mediaEditorSession) return true;
      if (this.isEditorDisabled) return true;
      if (this.isATwitterInbox) return true;
      if (
        this.hasAttachments ||
        this.hasRecordedAudio ||
        this.hasAttachedContacts
      ) {
        return false;
      }

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      if (this.isPrivate) {
        return MESSAGE_MAX_LENGTH.GENERAL;
      }
      if (this.isAFacebookInbox) {
        return MESSAGE_MAX_LENGTH.FACEBOOK;
      }
      if (this.isATelegramChannel) {
        return MESSAGE_MAX_LENGTH.TELEGRAM;
      }
      if (this.isATiktokChannel) {
        return MESSAGE_MAX_LENGTH.TIKTOK;
      }
      if (this.isATwilioWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
      }
      if (this.isAWhatsAppCloudChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      if (this.isASmsInbox) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAnEmailChannel) {
        return MESSAGE_MAX_LENGTH.EMAIL;
      }
      if (this.isATwilioSMSChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      const { image_send: imageSend } =
        this.currentChat?.additional_attributes?.tiktok_capabilities ?? {};
      const tiktokAttachmentSupported = imageSend ?? true;

      return (
        this.isAWebWidgetInbox ||
        this.isAFacebookInbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel ||
        this.isALineChannel ||
        this.isANotificaMeChannel ||
        this.isAnInstagramChannel ||
        this.channelType === INBOX_TYPES.INTERNAL ||
        (this.isATiktokChannel && tiktokAttachmentSupported)
      );
    },
    replyButtonLabel() {
      let sendMessageText = this.$t('CONVERSATION.REPLYBOX.SEND');
      if (this.isPrivate) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.CREATE');
      }
      const keyLabel = this.isEditorHotKeyEnabled('cmd_enter')
        ? `(${this.shortcutKey})`
        : '(↵)';
      return `${sendMessageText} ${keyLabel}`;
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        'is-compact': this.useCompactMessageComposer,
        'is-focused':
          this.isFocused || this.hasAttachments || this.hasAttachedContacts,
      };
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    hasAttachedContacts() {
      return this.attachedContacts.length;
    },
    showAudioRecorder() {
      return !this.isOnPrivateNote && this.showFileUpload;
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isOnPrivateNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      return !this.message.trim().replace(/\n/g, '').length;
    },
    showReplyHead() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    enableMultipleFileUpload() {
      return true;
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.messageSignature;
    },
    signaturePreferenceChannel() {
      return this.isAUnoapiChannel
        ? `${this.channelType} Unoapi`
        : this.channelType;
    },
    sendWithSignature() {
      if (this.isAUnoapiChannel) return false;

      return this.fetchSignatureFlagFromUISettings(
        this.signaturePreferenceChannel,
        false
      );
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return this.getDraftKey();
    },
    audioRecordFormat() {
      if (this.isAWhatsAppCloudChannel) {
        return AUDIO_FORMATS.OGG;
      }
      if (
        this.isAWhatsAppChannel ||
        this.isATelegramChannel ||
        this.isANotificaMeChannel
      ) {
        return AUDIO_FORMATS.MP3;
      }
      if (this.isAPIInbox) {
        return AUDIO_FORMATS.MP3;
      }
      return AUDIO_FORMATS.WAV;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
        contact: this.currentContact,
        inbox: this.inbox,
      });
      // Match the backend drops: names are Ruby-capitalized and
      // {{agent.*}} is the message sender, not the assignee.
      return {
        ...variables,
        ...getContactVariables(this.currentContact),
        ...getAgentVariables(this.currentUser),
      };
    },
    connectedPortalSlug() {
      const { help_center: portal = {} } = this.inbox;
      const { slug = '' } = portal;
      return slug;
    },
    quotedReplyPreference() {
      if (!this.isAnEmailChannel) {
        return false;
      }

      return !!this.fetchQuotedReplyFlagFromUISettings(this.channelType);
    },
    lastEmailWithQuotedContent() {
      if (!this.isAnEmailChannel) {
        return null;
      }

      const lastEmail = this.lastEmail;
      if (!lastEmail || lastEmail.private) {
        return null;
      }

      return lastEmail;
    },
    quotedEmailText() {
      return extractQuotedEmailText(this.lastEmailWithQuotedContent);
    },
    quotedEmailPreviewText() {
      return truncatePreviewText(this.quotedEmailText, 80);
    },
    shouldShowQuotedReplyToggle() {
      return this.isAnEmailChannel && !this.isOnPrivateNote;
    },
    shouldShowQuotedPreview() {
      return (
        this.shouldShowQuotedReplyToggle &&
        this.quotedReplyPreference &&
        !!this.quotedEmailText
      );
    },
    isDefaultEditorMode() {
      return !this.showAudioRecorderEditor && !this.copilot.isActive.value;
    },
    isEditorDisabled() {
      if (!this.isComposerActive) return true;
      if (this.isWebDestination && !this.isOnPrivateNote)
        return !this.webAttendanceActive;
      return (
        (this.isAWhatsAppChannel || this.isAPIInbox) &&
        !this.isOnPrivateNote &&
        !this.currentChat.can_reply
      );
    },
  },
  watch: {
    isComposerActive(active) {
      if (!active) {
        this.isFocused = false;
        this.saveDraft(this.conversationIdByRoute, this.replyType);
        this.showContactAttachmentModal = false;
        this.showWhatsAppTemplatesModal = false;
        this.showContentTemplatesModal = false;
        this.showScheduleModal = false;
        this.showStickerPicker = false;
        this.showEmojiPicker = false;
        if (this.isRecordingAudio && !this.hasRecordedAudio) {
          this.$refs.audioRecorderInput?.stopRecording();
        } else if (this.recordingAudioState === 'playing') {
          this.$refs.audioRecorderInput?.playPause();
        }
      }
    },
    currentChat(conversation, oldConversation) {
      const { can_reply: canReply } = conversation;
      if (oldConversation && oldConversation.id !== conversation.id) {
        // Only update email fields when switching to a completely different conversation (by ID)
        // This prevents overwriting user input (e.g., CC/BCC fields) when performing actions
        // like self-assign or other updates that do not actually change the conversation context
        this.setCCAndToEmailsFromLastChat();
        // Reset Copilot editor state (includes cancelling ongoing generation)
        this.copilot.reset();
      }

      if (this.managedDestinations) {
        if (oldConversation && oldConversation.id !== conversation.id) {
          this.inReplyTo = {};
        }
        return;
      }
      if (this.isOnPrivateNote) {
        return;
      }

      if (canReply || this.isAWhatsAppChannel || this.isAPIInbox) {
        this.replyType = REPLY_EDITOR_MODES.REPLY;
      } else {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
      }

      this.fetchAndSetReplyTo();
    },
    showGroupMentions(value) {
      if (value) this.fetchGroupMentionContacts(this.groupMentionSearchTerm());
      if (!value) this.groupMentionContacts = [];
    },
    message() {
      // Autosave the current message draft.
      this.doAutoSaveDraft();
      if (this.showGroupMentions) this.debouncedFetchGroupMentionContacts();
    },
    // When moving from one conversation to another, the store may not have the
    // list of all the messages. A fetch is subsequently made to get the messages.
    // This watcher handles two main cases:
    // 1. When switching conversations and messages are fetched/updated, ensures CC/BCC fields are set from the latest OUTGOING/INCOMING email (not activity/private messages).
    // 2. Fixes and issue where CC/BCC fields could be reset/lost after assignment/activity actions or message mutations that did not represent a true email context change.
    lastEmail: {
      handler(lastEmail) {
        if (!lastEmail) return;
        this.setCCAndToEmailsFromLastChat();
      },
      deep: true,
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.setToDraft(oldConversationId, this.replyType);
        this.getFromDraft();
        this.resetRecorderAndClearAttachments();
      }
    },
    replyType(updatedReplyType, oldReplyType) {
      this.setToDraft(this.conversationIdByRoute, oldReplyType);
      this.getFromDraft();
    },
  },

  mounted() {
    this.getFromDraft();
    // Don't use the keyboard listener mixin here as the events here are supposed to be
    // working even if the editor is focussed.
    document.addEventListener('paste', this.onPaste);
    document.addEventListener('keydown', this.handleKeyEvents);
    document.addEventListener('keydown', this.handleScheduleShortcut);
    this.setCCAndToEmailsFromLastChat();
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );

    this.fetchAndSetReplyTo();
    emitter.on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onReplyToMessage);

    // A hacky fix to solve the drag and drop
    // Is showing on top of new conversation modal drag and drop
    // TODO need to find a better solution
    emitter.on(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.on(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.on(BUS_EVENTS.NATIVE_SHARE_RECEIVED, this.onNativeShareReceived);
    emitter.on(CMD_AI_ASSIST, this.executeCopilotAction);
  },
  unmounted() {
    this.saveDraft(this.conversationIdByRoute, this.replyType);
    this.revokeAttachmentPreviews();
    clearTimeout(this.groupMentionFetchTimeout);
    document.removeEventListener('paste', this.onPaste);
    document.removeEventListener('keydown', this.handleKeyEvents);
    document.removeEventListener('keydown', this.handleScheduleShortcut);
    emitter.off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onReplyToMessage);
    emitter.off(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.off(BUS_EVENTS.NATIVE_SHARE_RECEIVED, this.onNativeShareReceived);
    emitter.off(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.off(CMD_AI_ASSIST, this.executeCopilotAction);
  },
  methods: {
    keydownWrapper(handler) {
      const callback = keyboardEventListenerMixins.methods.keydownWrapper.call(
        this,
        handler
      );
      return event => {
        if (this.isComposerActive) callback(event);
      };
    },
    onNativeShareReceived({ text, subject, files = [] }) {
      if (!this.isComposerActive) return;
      const sharedText = [subject, text].filter(Boolean).join('\n');
      if (sharedText) this.addIntoEditor(sharedText);

      files.forEach(file => {
        const isAllowed = isFileTypeAllowedForChannel(file, {
          channelType: this.channelType || this.inbox?.channel_type,
          medium: this.inbox?.medium,
          conversationType: this.conversationType,
          isInstagramChannel: this.isAnInstagramChannel,
          isOnPrivateNote: this.isOnPrivateNote,
        });
        if (!isAllowed) {
          useAlert(
            this.$t('CONVERSATION.FILE_TYPE_NOT_SUPPORTED', {
              fileName: file.name,
            })
          );
          return;
        }
        this.onFileUpload({
          name: file.name,
          type: file.type,
          size: file.size,
          file,
        });
      });
    },
    handleScheduleShortcut(event) {
      if (!this.isComposerActive || this.isWebDestination) return;
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key.toLowerCase() === 's' &&
        this.inbox?.channel_type === 'Channel::Whatsapp'
      ) {
        event.preventDefault();
        if (!this.isReplyButtonDisabled) this.openScheduleModal();
      }
    },
    groupMentionSearchTerm(message = this.message) {
      const match = message.match(/(?:^|\s)@([^\s@]*)$/);
      return match ? match[1] : '';
    },
    debouncedFetchGroupMentionContacts() {
      clearTimeout(this.groupMentionFetchTimeout);
      this.groupMentionFetchTimeout = setTimeout(() => {
        this.fetchGroupMentionContacts(this.groupMentionSearchTerm());
      }, 250);
    },
    async fetchGroupMentionContacts(query = '') {
      const normalizedQuery = query.trim();
      if (normalizedQuery.length < 2) {
        this.groupMentionContacts = [];
        return;
      }

      if (!this.canUseGroupMentions || this.isLoadingGroupMentionContacts) {
        this.groupMentionContacts = [];
        return;
      }

      this.isLoadingGroupMentionContacts = true;
      try {
        const { data } = await conversationApi.fetchGroupContacts(
          this.currentChat.id,
          1,
          normalizedQuery
        );
        this.groupMentionContacts = (data.payload || [])
          .map(member => this.normalizeGroupMentionContact(member))
          .filter(contact => contact.id && contact.bsuid);
      } finally {
        this.isLoadingGroupMentionContacts = false;
      }
    },
    normalizeGroupMentionContact(member = {}) {
      const contact = member.contact || {};
      const metadata = member.metadata || {};
      const phoneNumber =
        contact.phone_number?.replace(/\D/g, '') ||
        metadata.wa_id?.replace(/\D/g, '') ||
        (!member.participant_identifier?.includes('@')
          ? member.participant_identifier?.replace(/\D/g, '')
          : '');
      const bsuid =
        contact.bsuid ||
        metadata.user_id ||
        metadata.lid ||
        (metadata.jid?.endsWith('@lid') ? metadata.jid : '') ||
        phoneNumber;
      const name =
        contact.name ||
        contact.whatsapp_username ||
        metadata.name ||
        member.participant_identifier ||
        bsuid;

      return {
        id: contact.id,
        bsuid,
        name,
        displayName: name,
        whatsapp_username: contact.whatsapp_username,
        phone_number: contact.phone_number,
        thumbnail: contact.thumbnail,
      };
    },
    groupMentionAttributesFor(message = '') {
      if (!this.canUseGroupMentions || !message) return [];

      const mentionsByContactId = new Map(
        this.groupMentionContacts.map(contact => [
          contact.id?.toString(),
          contact,
        ])
      );

      return Array.from(message.matchAll(GROUP_CONTACT_MENTION_REGEX)).flatMap(
        match => {
          const contactId = match[2] || match[4];
          const mentionName = match[3] || match[5] || match[1];
          const contact = mentionsByContactId.get(contactId);
          if (!contact?.bsuid) return [];

          return {
            contact_id: contact.id,
            name: decodeURIComponent(mentionName || ''),
            bsuid: contact.bsuid,
          };
        }
      );
    },
    withGroupMentionsInPayload(payload, message = payload.message) {
      const groupMentions = this.groupMentionAttributesFor(message);
      if (!groupMentions.length) return payload;

      return {
        ...payload,
        contentAttributes: {
          ...(payload.contentAttributes || {}),
          group_mentions: groupMentions,
        },
      };
    },
    getDraftKey(
      conversationId = this.conversationIdByRoute,
      replyType = this.replyType
    ) {
      return attendanceDraftKey(
        conversationId,
        replyType,
        this.deliveryDestination
      );
    },
    getCopilotAcceptedMessage(replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      return this.copilotAcceptedMessages[key] || '';
    },
    setCopilotAcceptedMessage(message, replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      this.copilotAcceptedMessages[key] = trimContent(
        message || '',
        this.maxLength
      );
    },
    clearCopilotAcceptedMessage(replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      delete this.copilotAcceptedMessages[key];
    },
    handleInsert(article) {
      const { url, title } = article;
      // Removing empty lines from the title
      const lines = title.split('\n');
      const nonEmptyLines = lines.filter(line => line.trim() !== '');
      const filteredMarkdown = nonEmptyLines.join(' ');
      emitter.emit(
        BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
        `[${filteredMarkdown}](${url})`
      );

      useTrack(CONVERSATION_EVENTS.INSERT_ARTICLE_LINK);
    },
    toggleQuotedReply() {
      if (!this.isAnEmailChannel) {
        return;
      }

      const nextValue = !this.quotedReplyPreference;
      this.setQuotedReplyFlagForInbox(this.channelType, nextValue);
    },
    shouldIncludeQuotedEmail() {
      return (
        this.quotedReplyPreference &&
        this.shouldShowQuotedReplyToggle &&
        !!this.quotedEmailText
      );
    },
    getMessageWithQuotedEmailText(message) {
      if (!this.shouldIncludeQuotedEmail()) {
        return message;
      }

      const quotedText = this.quotedEmailText || '';
      const header = buildQuotedEmailHeader(
        this.lastEmailWithQuotedContent,
        this.currentContact,
        this.inbox
      );

      return appendQuotedTextToMessage(message, quotedText, header);
    },
    resetRecorderAndClearAttachments() {
      // Reset audio recorder UI state
      this.resetAudioRecorderInput();
      // Reset attached files
      this.clearAttachedFiles();
      this.attachedContacts = [];
    },
    saveDraft(conversationId, replyType) {
      if (this.message || this.message === '') {
        const key = this.getDraftKey(conversationId, replyType);
        const draftToSave = trimContent(this.message || '', this.maxLength);

        this.$store.dispatch('draftMessages/set', {
          key,
          message: draftToSave,
        });
      }
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    getFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        const messageFromStore =
          this.$store.getters['draftMessages/get'](key) || '';

        // ensure that the message has signature set based on the ui setting
        this.message = this.toggleSignatureForDraft(messageFromStore);
      }
    },
    toggleSignatureForDraft(message) {
      // Even when editor is disabled (e.g. WhatsApp/API can't reply), we must
      // still normalize stale signatures out of drafts when signature is off.
      if (this.isEditorDisabled && this.sendWithSignature) {
        return message;
      }

      const effectiveChannelType = getEffectiveChannelType(
        this.channelType,
        this.inbox?.medium || ''
      );
      return this.sendWithSignature
        ? appendSignature(message, this.messageSignature, effectiveChannelType)
        : removeSignature(message, this.messageSignature, effectiveChannelType);
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    getElementToBind() {
      return this.replyEditor;
    },
    getKeyboardEvents() {
      return {
        Escape: {
          action: () => {
            this.hideEmojiPicker();
          },
          allowOnFocusedInput: true,
        },
        '$mod+KeyK': {
          action: e => {
            e.preventDefault();
            const ninja = document.querySelector('ninja-keys');
            ninja.open();
          },
          allowOnFocusedInput: true,
        },
        Enter: {
          action: e => {
            if (this.isAValidEvent('enter')) {
              this.onSendReply();
              e.preventDefault();
            }
          },
          allowOnFocusedInput: true,
        },
        '$mod+Enter': {
          action: () => {
            if (this.copilot.isActive.value && this.isFocused) {
              this.onSubmitCopilotReply();
            } else if (this.isAValidEvent('cmd_enter')) {
              this.onSendReply();
            }
          },
          allowOnFocusedInput: true,
        },
        '$mod+KeyM': {
          action: event => {
            this.handleCaptainShortcut(event, 'improve');
          },
          allowOnFocusedInput: true,
        },
        '$mod+KeyO': {
          action: event => {
            this.handleCaptainShortcut(event, 'fix_spelling_grammar');
          },
          allowOnFocusedInput: true,
        },
      };
    },
    handleCaptainShortcut(event, action) {
      if (!this.isFocused) return;

      event.preventDefault();
      if (
        event.repeat ||
        !this.captainTasksEnabled ||
        !this.hasMeaningfulEditorContent ||
        this.isEditorDisabled ||
        this.copilot.isActive.value
      ) {
        return;
      }

      this.executeCopilotAction(action, this.message);
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showGroupMentions &&
        !this.showMentions &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        this.isFocused &&
        this.isEditorHotKeyEnabled(selectedKey)
      );
    },
    onPaste(e) {
      if (!this.isComposerActive) return;
      // Don't handle paste if compose new conversation modal is open
      if (this.newConversationModalActive) return;

      // Don't handle paste if editor is disabled
      if (this.isEditorDisabled) return;
      if (!this.showFileUpload && !this.isOnPrivateNote) return;

      // Filter valid files (non-zero size)
      Array.from(e.clipboardData.files)
        .filter(file => file.size > 0)
        .filter(file => {
          const isAllowed = isFileTypeAllowedForChannel(file, {
            channelType: this.channelType || this.inbox?.channel_type,
            medium: this.inbox?.medium,
            conversationType: this.conversationType,
            isInstagramChannel: this.isAnInstagramChannel,
            isOnPrivateNote: this.isOnPrivateNote,
          });

          if (!isAllowed) {
            useAlert(
              this.$t('CONVERSATION.FILE_TYPE_NOT_SUPPORTED', {
                fileName: file.name,
              })
            );
          }

          return isAllowed;
        })
        .forEach(file => {
          const { name, type, size } = file;
          this.onFileUpload({ name, type, size, file });
        });
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleGroupMention(currentMentionState) {
      this.showGroupMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    openWhatsappTemplateModal() {
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
    },
    openContentTemplateModal() {
      this.showContentTemplatesModal = true;
    },
    hideContentTemplatesModal() {
      this.showContentTemplatesModal = false;
    },
    openContactAttachmentModal() {
      this.showContactAttachmentModal = true;
    },
    hideContactAttachmentModal() {
      this.showContactAttachmentModal = false;
    },
    setAttachedContacts(contacts) {
      this.attachedContacts = contacts;
      this.hideContactAttachmentModal();
    },
    removeAttachedContact(contactId) {
      this.attachedContacts = this.attachedContacts.filter(
        contact => contact.id !== contactId
      );
    },
    showStickerPickerModal() {
      // eslint-disable-next-line no-console
      console.info('[StickerPicker] open modal', {
        conversationId: this.currentChat?.id,
        inboxId: this.inboxId,
      });
      this.showStickerPicker = true;
    },
    openScheduleModal() {
      if (this.isWebDestination) return;
      this.scheduledAt = this.toDatetimeLocal(new Date(Date.now() + 300000));
      this.scheduledLabelId = '';
      this.scheduledReason = '';
      this.scheduledSenderId = this.currentUser.id;
      this.showScheduleLabelDropdown = false;
      this.scheduleUploadCount = 0;
      this.scheduledItems = [
        {
          content: this.message,
          content_type: 'text',
          content_attributes: {},
          voice_message: this.attachedFiles.some(file => file?.isRecordedAudio),
          attachments: this.attachedFiles
            .map(file => ({
              signedId:
                file?.blobSignedId || file?.blob?.signed_id || file?.signed_id,
              name: file?.resource?.filename || file?.name || 'Anexo',
              voiceMessage: Boolean(file?.isRecordedAudio),
            }))
            .filter(file => file.signedId),
        },
      ];
      this.showScheduleModal = true;
    },
    selectScheduleLabel(label) {
      this.scheduledLabelId = label.id;
      this.showScheduleLabelDropdown = false;
    },
    toDatetimeLocal(date) {
      const timezoneOffset = date.getTimezoneOffset() * 60000;
      return new Date(date.getTime() - timezoneOffset)
        .toISOString()
        .slice(0, 16);
    },
    async createScheduledMessage() {
      if (!this.scheduledLabelId || !this.scheduledAt || !this.isScheduleValid)
        return;
      try {
        await scheduledMessagesApi.create({
          scheduled_message: {
            scheduled_at: new Date(this.scheduledAt).toISOString(),
            label_id: this.scheduledLabelId,
            reason: this.scheduledReason,
            sender_id: this.scheduledSenderId,
            messages: this.scheduledItems.map(item => ({
              content: item.content,
              content_type: item.content_type || 'text',
              content_attributes: item.content_attributes || {},
              voice_message: Boolean(item.voice_message),
              attachment_blob_ids: item.attachments.map(
                attachment => attachment.signedId
              ),
            })),
          },
          conversation_id: this.currentChat.id,
        });
        this.clearMessage();
        this.attachedFiles = [];
        this.showScheduleModal = false;
        useAlert('Mensagem agendada');
      } catch (error) {
        useAlert(
          error?.response?.data?.error || 'Não foi possível agendar a mensagem'
        );
      }
    },
    onScheduleFileUpload({ index, file, voiceMessage }) {
      if (!file?.file) return;
      const maxSizeMB = this.maxSizeFor(file.file.type);
      if (!checkFileSizeLimit(file, maxSizeMB)) {
        this.alertOverLimit(maxSizeMB);
        return;
      }

      this.scheduleUploadCount += 1;
      const upload = new DirectUpload(
        file.file,
        getDirectUploadUrl(
          `/api/v1/accounts/${this.accountId}/conversations/${this.currentChat.id}/direct_uploads`
        ),
        {
          directUploadWillCreateBlobWithXHR: xhr => {
            setDirectUploadAuthHeaders(xhr);
          },
        }
      );
      upload.create((error, blob) => {
        this.scheduleUploadCount -= 1;
        if (error) {
          useAlert(error);
          return;
        }
        const item = this.scheduledItems[index];
        if (!item) return;
        const attachment = {
          signedId: blob.signed_id,
          name: blob.filename || file.name,
          voiceMessage,
        };
        const attachments = [...item.attachments, attachment];
        this.scheduledItems = this.scheduledItems.map((current, itemIndex) =>
          itemIndex === index
            ? {
                ...current,
                attachments,
                voice_message: current.voice_message || Boolean(voiceMessage),
              }
            : current
        );
      });
    },
    hideStickerPickerModal() {
      // eslint-disable-next-line no-console
      console.info('[StickerPicker] close modal');
      this.showStickerPicker = false;
    },
    sendStickerMessage(sticker) {
      // eslint-disable-next-line no-console
      console.info('[StickerPicker] send message', {
        conversationId: this.currentChat.id,
        stickerId: sticker.id,
      });
      this.sendMessage({
        conversationId: this.currentChat.id,
        content_type: 'sticker',
        content_attributes: {
          sticker_id: sticker.id,
          sticker_url: sticker.file_url,
        },
      });
    },
    confirmOnSendReply() {
      const invalidContact = this.attachedContacts
        .map(contact => this.serializeAttachedContact(contact))
        .find(
          contact =>
            !contact.formatted_name?.trim() ||
            (!contact.phone_number?.trim() && !contact.email?.trim())
        );
      if (invalidContact) {
        useAlert(
          this.$t('CONVERSATION.CONTACT_BUNDLE.INVALID', {
            name:
              invalidContact.formatted_name ||
              this.$t('CONVERSATION.CONTACT_BUNDLE.TITLE'),
          })
        );
        return;
      }
      if (this.isReplyButtonDisabled) {
        return;
      }
      if (!this.showMentions) {
        const copilotAcceptedMessage = this.getCopilotAcceptedMessage();
        const isOnWhatsApp =
          this.isATwilioWhatsAppChannel ||
          this.isAWhatsAppCloudChannel ||
          this.isAUnoapiChannel ||
          this.is360DialogWhatsAppChannel;
        // When users send messages containing both text and attachments on Instagram, Instagram treats them as separate messages.
        // Although ViperChat combines these into a single message, Instagram sends separate echo events for each component.
        // This can create duplicate messages in ViperChat. To prevent this issue, we'll handle text and attachments as separate messages.
        const isOnInstagram = this.isAnInstagramChannel;
        const isOnTiktok = this.isATiktokChannel;
        if ((isOnWhatsApp || isOnInstagram || isOnTiktok) && !this.isPrivate) {
          this.sendMessageAsMultipleMessages(
            this.message,
            copilotAcceptedMessage
          );
        } else {
          const messagePayload = this.getMessagePayload(this.message);
          this.sendMessage(
            messagePayload,
            this.message,
            copilotAcceptedMessage
          );
        }

        if (!this.isPrivate) {
          this.clearEmailField();
        }

        this.clearMessage();
        this.hideEmojiPicker();
        this.$emit('update:popOutReplyBox', false);
      }
    },
    sendMessageAsMultipleMessages(message, copilotAcceptedMessage = '') {
      const messages = this.getMultipleMessagesPayload(message);
      messages.forEach(messagePayload => {
        this.sendMessage(
          messagePayload,
          messagePayload.message || '',
          copilotAcceptedMessage
        );
      });
    },
    sendMessageAnalyticsData(
      isPrivate,
      { editorMessage = '', copilotAcceptedMessage = '' } = {}
    ) {
      const normalizeForComparison = message => {
        let normalizedMessage = message || '';

        if (this.sendWithSignature && this.messageSignature && !isPrivate) {
          const effectiveChannelType = getEffectiveChannelType(
            this.channelType,
            this.inbox?.medium || ''
          );
          normalizedMessage = removeSignature(
            normalizedMessage,
            this.messageSignature,
            effectiveChannelType
          );
        }

        return trimContent(normalizedMessage);
      };

      const normalizedAcceptedMessage = normalizeForComparison(
        copilotAcceptedMessage
      );
      const normalizedEditorMessage = normalizeForComparison(editorMessage);

      if (normalizedAcceptedMessage && normalizedEditorMessage) {
        useTrack(CAPTAIN_EVENTS.AI_ASSISTED_MESSAGE_SENT, {
          conversationId: this.conversationIdByRoute,
          channelType: this.channelType,
          editedBeforeSend:
            normalizedAcceptedMessage !== normalizedEditorMessage,
          isPrivate,
        });
      }

      // Analytics data for message signature is enabled or not in channels
      return isPrivate
        ? useTrack(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : useTrack(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });
    },
    async onSendReply() {
      if (this.isComposerActive === false || this.isEditorDisabled) return;
      if (this.hasRecordedAudio && this.isRecordedAudioUploadPending) {
        this.sendRecordedAudioAfterUpload = true;
        return;
      }

      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          this.confirmOnSendReply();
        }
      } else {
        this.confirmOnSendReply();
      }
    },
    async sendPixPayment() {
      if (!this.showPixPaymentButton) return;

      const confirmed =
        await this.$refs.pixPaymentConfirmDialog.showConfirmation();
      if (!confirmed) return;

      await this.sendMessage({
        conversationId: this.currentChat.id,
        message: this.pixPaymentMessageContent,
        private: false,
        contentType: 'text',
        contentAttributes: {
          whatsapp_interactive: { type: 'payment_request' },
        },
      });
    },
    async sendMessage(
      messagePayload,
      editorMessage = '',
      copilotAcceptedMessage = ''
    ) {
      try {
        await this.$store.dispatch('createPendingMessageAndSend', {
          ...messagePayload,
          delivery_destination: messagePayload.private
            ? 'channel'
            : this.deliveryDestination || 'channel',
        });
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
        this.removeFromDraft();
        this.sendMessageAnalyticsData(messagePayload.private, {
          editorMessage,
          copilotAcceptedMessage,
        });
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage);
      }
    },
    async onSendWhatsAppReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    async onSendContentTemplateReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideContentTemplatesModal();
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      if (this.managedDestinations) {
        this.$emit('setReplyMode', mode);
        return;
      }
      // Clear attachments when switching between private note and reply modes
      // This is to prevent from breaking the upload rules
      if (this.attachedFiles.length > 0) this.clearAttachedFiles();
      if (this.attachedContacts.length > 0) this.attachedContacts = [];

      const { can_reply: canReply } = this.currentChat;
      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode,
      });
      if (canReply || this.isAWhatsAppChannel || this.isAPIInbox)
        this.replyType = mode;
      if (this.isRecordingAudio) {
        this.toggleAudioRecorder();
      }
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    addIntoEditor(content) {
      if (!this.isComposerActive) return;
      this.updateEditorSelectionWith = content;
      this.onFocus();
    },
    executeCopilotAction(action, data) {
      if (!this.isComposerActive) return;
      this.copilot.execute(action, data);
    },
    clearMessage() {
      this.message = '';
      this.clearCopilotAcceptedMessage();
      if (this.sendWithSignature && !this.isPrivate) {
        // if signature is enabled, append it to the message
        const effectiveChannelType = getEffectiveChannelType(
          this.channelType,
          this.inbox?.medium || ''
        );
        this.message = appendSignature(
          this.message,
          this.messageSignature,
          effectiveChannelType
        );
      }
      this.clearAttachedFiles();
      this.attachedContacts = [];
      this.isRecordingAudio = false;
      this.isRecordedAudioUploadPending = false;
      this.sendRecordedAudioAfterUpload = false;
      this.resetReplyToMessage();
      this.resetAudioRecorderInput();
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
      this.toEmails = '';
    },

    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.resetAudioRecorderInput();
      }
    },
    toggleAudioRecorderPlayPause() {
      if (!this.$refs.audioRecorderInput) return;
      if (!this.recordingAudioState) {
        this.$refs.audioRecorderInput.stopRecording();
      } else {
        this.$refs.audioRecorderInput.playPause();
      }
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraft(this.conversationIdByRoute, this.replyType);
    },
    onFocus() {
      this.isFocused = true;
    },
    onRecordProgressChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    async onFinishRecorder(file) {
      this.recordingAudioState = 'stopped';
      this.hasRecordedAudio = true;
      // Added a new key isRecordedAudio to the file to find it's and recorded audio
      // Because to filter and show only non recorded audio and other attachments
      const autoRecordedFile = {
        ...file,
        isRecordedAudio: true,
      };
      if (!file) {
        this.hasRecordedAudio = false;
        return;
      }

      this.isRecordedAudioUploadPending = true;
      const wasAttached = await this.onFileUpload(autoRecordedFile);
      this.isRecordedAudioUploadPending = false;

      if (!wasAttached) {
        this.hasRecordedAudio = false;
        this.sendRecordedAudioAfterUpload = false;
        return;
      }

      if (this.sendRecordedAudioAfterUpload) {
        this.sendRecordedAudioAfterUpload = false;
        await this.onSendReply();
      }
    },
    onAudioRecorderError() {
      // getUserMedia can reject after Android's runtime permission dialog.
      // Always leave the recorder state so the user can retry immediately.
      this.resetAudioRecorderInput();
      useAlert(this.$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ERROR'));
    },
    toggleTyping(status) {
      if (!this.isComposerActive || this.isWebDestination) return;
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });
    },
    attachFile({ blob, file }) {
      if (!this.showFileUpload && !this.isOnPrivateNote) return false;

      if (!this.enableMultipleFileUpload && this.attachedFiles.length > 0) {
        useAlert(this.$t('CONVERSATION.REPLYBOX.TIP_ATTACH_SINGLE'));
        return false;
      }
      const previewObjectUrl = URL.createObjectURL(file.file);
      this.attachedFiles.push({
        currentChatId: this.currentChat.id,
        resource: blob || file,
        isPrivate: this.isPrivate,
        thumb: previewObjectUrl,
        previewObjectUrl,
        blobSignedId: blob ? blob.signed_id : undefined,
        isRecordedAudio: file?.isRecordedAudio || false,
      });
      return true;
    },
    revokeAttachmentPreview(attachment) {
      if (attachment?.previewObjectUrl) {
        URL.revokeObjectURL(attachment.previewObjectUrl);
      }
    },
    revokeAttachmentPreviews(attachments = this.attachedFiles) {
      attachments.forEach(this.revokeAttachmentPreview);
    },
    clearAttachedFiles() {
      this.revokeAttachmentPreviews();
      this.attachedFiles = [];
    },
    removeAttachment(attachments) {
      const retainedAttachments = new Set(attachments);
      this.attachedFiles
        .filter(attachment => !retainedAttachments.has(attachment))
        .forEach(this.revokeAttachmentPreview);
      this.attachedFiles = attachments;
    },
    serializeAttachedContact(contact) {
      const fullName =
        contact.formattedName ||
        contact.name ||
        [contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
        '';
      const [firstName, ...lastNameParts] = fullName.split(' ').filter(Boolean);

      return {
        id: contact.id,
        formatted_name: fullName,
        first_name: firstName || fullName,
        last_name: lastNameParts.join(' ') || '',
        phone_number: contact.phoneNumber || contact.phone_number || '',
        email: contact.email || '',
      };
    },
    setReplyToInPayload(payload) {
      if (this.inReplyTo?.id) {
        return {
          ...payload,
          contentAttributes: {
            ...payload.contentAttributes,
            in_reply_to: this.inReplyTo.id,
          },
        };
      }

      return payload;
    },
    getMultipleMessagesPayload(message) {
      const multipleMessagePayload = [];

      if (this.attachedContacts.length) {
        let contactsPayload = {
          conversationId: this.currentChat.id,
          private: false,
          sender: this.sender,
          contentType: 'text',
          contentAttributes: {
            contacts: this.attachedContacts.map(contact =>
              this.serializeAttachedContact(contact)
            ),
          },
        };

        contactsPayload = this.setReplyToInPayload(contactsPayload);
        contactsPayload = this.withGroupMentionsInPayload(
          contactsPayload,
          contactsPayload.message
        );
        multipleMessagePayload.push(contactsPayload);
      }

      if (this.attachedFiles && this.attachedFiles.length) {
        let caption =
          this.isAnInstagramChannel || this.isATiktokChannel ? '' : message;
        this.attachedFiles.forEach(attachment => {
          const attachedFile = attachment.blobSignedId
            ? attachment.blobSignedId
            : attachment.resource.file;
          let attachmentPayload = {
            conversationId: this.currentChat.id,
            files: [attachedFile],
            private: false,
            message: caption,
            sender: this.sender,
          };

          attachmentPayload = this.setReplyToInPayload(attachmentPayload);
          if (
            this.isAUnoapiChannel &&
            attachment.isRecordedAudio &&
            this.recordedAudioViewOnce
          ) {
            attachmentPayload.contentAttributes = {
              ...attachmentPayload.contentAttributes,
              view_once: true,
            };
          }
          attachmentPayload = this.withGroupMentionsInPayload(
            attachmentPayload,
            attachmentPayload.message
          );
          multipleMessagePayload.push(attachmentPayload);
          // For WhatsApp, only the first attachment gets a caption
          if (!this.isAnInstagramChannel) caption = '';
        });
      }

      const hasNoAttachments =
        !this.attachedFiles || !this.attachedFiles.length;
      // For Instagram and TikTok, text must always be sent as a separate message (no captions on attachments).
      // For WhatsApp, text is sent separately only when there are no file attachments.
      if (
        ((this.isAnInstagramChannel || this.isATiktokChannel) && message) ||
        (!(this.isAnInstagramChannel || this.isATiktokChannel) &&
          hasNoAttachments &&
          message)
      ) {
        let messagePayload = {
          conversationId: this.currentChat.id,
          message,
          private: false,
          sender: this.sender,
        };

        messagePayload = this.setReplyToInPayload(messagePayload);
        messagePayload = this.withGroupMentionsInPayload(
          messagePayload,
          messagePayload.message
        );

        multipleMessagePayload.push(messagePayload);
      }

      return multipleMessagePayload;
    },
    getMessagePayload(message) {
      const messageWithQuote = this.getMessageWithQuotedEmailText(message);

      let messagePayload = {
        conversationId: this.currentChat.id,
        message: messageWithQuote,
        private: this.isPrivate,
        sender: this.sender,
      };
      messagePayload = this.setReplyToInPayload(messagePayload);
      messagePayload = this.withGroupMentionsInPayload(
        messagePayload,
        messageWithQuote
      );

      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = [];
        this.attachedFiles.forEach(attachment => {
          if (attachment.blobSignedId) {
            messagePayload.files.push(attachment.blobSignedId);
          } else {
            messagePayload.files.push(attachment.resource.file);
          }
        });
      }

      if (this.attachedContacts.length) {
        messagePayload.contentType = 'text';
        messagePayload.contentAttributes = {
          ...(messagePayload.contentAttributes || {}),
          contacts: this.attachedContacts.map(contact =>
            this.serializeAttachedContact(contact)
          ),
        };
      }

      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      if (this.toEmails && !this.isOnPrivateNote) {
        messagePayload.toEmails = this.toEmails;
      }
      return messagePayload;
    },
    setCcEmails(value) {
      this.bccEmails = value.bccEmails;
      this.ccEmails = value.ccEmails;
    },
    setCCAndToEmailsFromLastChat() {
      const conversationContact = this.currentChat?.meta?.sender?.email || '';
      const { email: inboxEmail, forward_to_email: forwardToEmail } =
        this.inbox;

      const { cc, bcc, to } = getRecipients(
        this.lastEmail,
        conversationContact,
        inboxEmail,
        forwardToEmail
      );

      this.toEmails = to.join(', ');
      this.ccEmails = cc.join(', ');
      this.bccEmails = bcc.join(', ');
    },
    fetchAndSetReplyTo() {
      if (!this.isComposerActive) return;
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const replyToMessageId = LocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );

      this.inReplyTo = this.currentChat?.messages?.find(message => {
        if (String(message.id) === String(replyToMessageId)) {
          return true;
        }
        return false;
      });
    },
    onReplyToMessage(message) {
      if (!this.isComposerActive) return;
      this.inReplyTo = message || null;
      if (!this.inReplyTo) {
        this.fetchAndSetReplyTo();
      }
      if (this.inReplyTo) {
        this.$nextTick(() => {
          const pos = this.isSignatureEnabledForInbox ? 'start' : 'end';
          this.messageEditor?.focusEditorInputField(pos);
        });
      }
    },
    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      LocalStorage.deleteFromJsonStore(replyStorageKey, this.conversationId);
      emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
    onNewConversationModalActive(isActive) {
      // Issue is if the new conversation modal is open and we drag and drop the file
      // then the file is not getting attached to the new conversation modal
      // and it is getting attached to the current conversation reply box
      // so to fix this we are removing the drag and drop event listener from the current conversation reply box
      // When new conversation modal is open
      this.newConversationModalActive = isActive;
    },
    onSearchPopoverClose() {
      this.showArticleSearchPopover = false;
    },
    toggleInsertArticle() {
      this.showArticleSearchPopover = !this.showArticleSearchPopover;
    },
    resetAudioRecorderInput() {
      this.recordedAudioViewOnce = false;
      this.recordingAudioDurationText = '00:00';
      this.isRecordingAudio = false;
      this.recordingAudioState = '';
      this.hasRecordedAudio = false;
      this.isRecordedAudioUploadPending = false;
      this.sendRecordedAudioAfterUpload = false;
      // Only clear the recorded audio when we click toggle button.
      const recordedAudioFiles = this.attachedFiles.filter(
        file => file?.isRecordedAudio
      );
      recordedAudioFiles.forEach(this.revokeAttachmentPreview);
      this.attachedFiles = this.attachedFiles.filter(
        file => !file?.isRecordedAudio
      );
    },
    cancelAudioRecorder() {
      this.isRecordingAudio = false;
      this.resetAudioRecorderInput();
    },
    restartAudioRecorder() {
      this.cancelAudioRecorder();
      this.$nextTick(() => {
        this.isRecordingAudio = true;
      });
    },
    togglePopout() {
      this.$emit('update:popOutReplyBox', !this.popOutReplyBox);
    },
    toggleEditorSize() {
      this.$emit('toggleEditorSize');
      this.$nextTick(() => this.messageEditor?.focusEditorInputField());
    },
    onSubmitCopilotReply() {
      const acceptedMessage = this.copilot.accept();
      this.message = acceptedMessage;
      this.setCopilotAcceptedMessage(acceptedMessage);
    },
  },
};
</script>

<template>
  <MediaEditor
    v-if="mediaEditorSession && isComposerActive"
    :files="mediaEditorFiles"
    :caption="mediaEditorSession.caption"
    :allow-view-once="isAUnoapiChannel"
    :recipient="currentChat.meta?.sender?.name || ''"
    :max-caption="Math.min(1024, maxLength)"
    :max-video-output-bytes="
      Math.min(256, maxSizeFor('video/mp4')) * 1024 * 1024
    "
    :submit-file="submitEditedMedia"
    @close="closeMediaEditor"
  />
  <ReplyBoxBanner :message="message" :is-on-private-note="isOnPrivateNote" />
  <div ref="replyEditor" class="reply-box" :class="replyBoxClass">
    <ReplyTopPanel
      v-if="!useCompactMessageComposer"
      :mode="replyType"
      :conversation-id="conversationId"
      :is-reply-restricted="isReplyRestricted"
      :disabled="
        (copilot.isActive.value && copilot.isButtonDisabled.value) ||
        showAudioRecorderEditor
      "
      :is-editor-disabled="isEditorDisabled"
      :is-message-length-reaching-threshold="isMessageLengthReachingThreshold"
      :characters-remaining="charactersRemaining"
      :editor-content="message"
      :popout-reply-box="popOutReplyBox"
      :has-content="hasMeaningfulEditorContent"
      :show-pix-button="showPixPaymentButton"
      @set-reply-mode="setReplyMode"
      @toggle-editor-size="toggleEditorSize"
      @toggle-popout="togglePopout"
      @toggle-copilot="copilot.toggleEditor"
      @execute-copilot-action="executeCopilotAction"
      @send-pix-payment="sendPixPayment"
    />
    <ArticleSearchPopover
      v-if="showArticleSearchPopover && connectedPortalSlug"
      :selected-portal-slug="connectedPortalSlug"
      @insert="handleInsert"
      @close="onSearchPopoverClose"
    />
    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    >
      <div :key="copilot.editorTransitionKey.value" class="reply-box__top">
        <ReplyToMessage
          v-if="shouldShowReplyToMessage"
          :message="inReplyTo"
          @dismiss="resetReplyToMessage"
        />
        <EmojiIconPicker
          v-if="showEmojiPicker"
          v-on-clickaway="hideEmojiPicker"
          mode="emoji"
          class="emoji-dialog"
          :class="{
            'emoji-dialog--expanded': isOnExpandedLayout || popOutReplyBox,
          }"
          @select="addIntoEditor($event.value)"
        />
        <ReplyEmailHead
          v-if="showReplyHead && isDefaultEditorMode"
          v-model:cc-emails="ccEmails"
          v-model:bcc-emails="bccEmails"
          v-model:to-emails="toEmails"
        />
        <CompactReplyComposer
          v-if="useCompactMessageComposer"
          :mode="replyType"
          :conversation-id="conversationId"
          :inbox="inbox"
          :delivery-destination="deliveryDestination"
          :editor-content="message"
          :has-content="hasComposerContent"
          :is-send-disabled="isReplyButtonDisabled"
          :is-editor-disabled="isEditorDisabled"
          :is-reply-restricted="isReplyRestricted"
          :show-file-upload="showFileUpload"
          :show-audio-recorder="showAudioRecorder"
          :enable-multiple-file-upload="enableMultipleFileUpload"
          :new-conversation-modal-active="
            newConversationModalActive || !isComposerActive
          "
          :conversation-type="conversationType"
          :enable-whats-app-templates="showWhatsappTemplates"
          :enable-content-templates="showContentTemplates"
          :portal-slug="connectedPortalSlug"
          :show-pix-button="showPixPaymentButton"
          :show-quoted-reply-toggle="shouldShowQuotedReplyToggle"
          :quoted-reply-enabled="quotedReplyPreference"
          :signature-preference-channel="signaturePreferenceChannel"
          :is-message-length-reaching-threshold="
            isMessageLengthReachingThreshold
          "
          :characters-remaining="charactersRemaining"
          :is-recording-audio="isRecordingAudio"
          :recording-audio-state="recordingAudioState"
          :recording-audio-duration-text="recordingAudioDurationText"
          :has-recorded-audio="hasRecordedAudio"
          :allow-audio-view-once="isAUnoapiChannel && !isPrivate"
          :audio-view-once="recordedAudioViewOnce"
          :is-recorded-audio-send-pending="sendRecordedAudioAfterUpload"
          :is-copilot-active="copilot.isActive.value"
          :on-file-upload="onFileUpload"
          :send-button-text="replyButtonLabel"
          @update:audio-view-once="recordedAudioViewOnce = $event"
          @cancel-audio-recorder="cancelAudioRecorder"
          @execute-copilot-action="executeCopilotAction"
          @open-contact-picker="openContactAttachmentModal"
          @restart-audio-recorder="restartAudioRecorder"
          @select-content-template="openContentTemplateModal"
          @select-whatsapp-template="openWhatsappTemplateModal"
          @send="onSendReply"
          @send-pix-payment="sendPixPayment"
          @set-reply-mode="setReplyMode"
          @toggle-audio-recorder="toggleAudioRecorder"
          @toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
          @toggle-emoji-picker="toggleEmojiPicker"
          @toggle-insert-article="toggleInsertArticle"
          @toggle-quoted-reply="toggleQuotedReply"
          @toggle-sticker-picker="showStickerPickerModal"
          @schedule="openScheduleModal"
        >
          <template #audio>
            <AudioRecorder
              v-if="showAudioRecorderEditor"
              ref="audioRecorderInput"
              :audio-record-format="audioRecordFormat"
              :height="32"
              @recorder-progress-changed="onRecordProgressChanged"
              @finish-record="onFinishRecorder"
              @record-error="onAudioRecorderError"
              @play="recordingAudioState = 'playing'"
              @pause="recordingAudioState = 'paused'"
            />
          </template>
          <CopilotEditorSection
            v-if="copilot.isActive.value && !showAudioRecorderEditor"
            :show-copilot-editor="copilot.showEditor.value"
            :is-generating-content="copilot.isGenerating.value"
            :generated-content="copilot.generatedContent.value"
            :is-popout="popOutReplyBox"
            :placeholder="$t('CONVERSATION.FOOTER.COPILOT_MSG_INPUT')"
            @focus="onFocus"
            @blur="onBlur"
            @clear-selection="clearEditorSelection"
            @close="copilot.showEditor.value = false"
            @content-ready="copilot.setContentReady"
            @send="copilot.sendFollowUp"
          />
          <WootMessageEditor
            v-else-if="!showAudioRecorderEditor && isComposerActive"
            ref="messageEditor"
            v-model="message"
            :conversation-id="conversationId"
            :editor-id="editorStateId"
            class="input popover-prosemirror-menu compact-message-editor"
            :is-private="isOnPrivateNote"
            :placeholder="messagePlaceHolder"
            :aria-label="composerAriaLabel"
            :update-selection-with="updateEditorSelectionWith"
            :disabled="isEditorDisabled"
            :focus-on-mount="shouldFocusMessageEditorOnMount"
            enable-variables
            :variables="messageVariables"
            :signature="messageSignature"
            :allow-signature="!isAUnoapiChannel"
            :signature-preference-channel="signaturePreferenceChannel"
            :enable-group-mentions="canUseGroupMentions"
            :group-mention-contacts="groupMentionContacts"
            :channel-type="channelType"
            :medium="inbox.medium"
            @typing-off="onTypingOff"
            @typing-on="onTypingOn"
            @focus="onFocus"
            @blur="onBlur"
            @toggle-user-mention="toggleUserMention"
            @toggle-group-mention="toggleGroupMention"
            @toggle-canned-menu="toggleCannedMenu"
            @toggle-variables-menu="toggleVariablesMenu"
            @clear-selection="clearEditorSelection"
            @execute-copilot-action="executeCopilotAction"
          />
        </CompactReplyComposer>

        <template v-else>
          <AudioRecorder
            v-if="showAudioRecorderEditor"
            ref="audioRecorderInput"
            :audio-record-format="audioRecordFormat"
            @recorder-progress-changed="onRecordProgressChanged"
            @finish-record="onFinishRecorder"
            @record-error="onAudioRecorderError"
            @play="recordingAudioState = 'playing'"
            @pause="recordingAudioState = 'paused'"
          />
          <CopilotEditorSection
            v-if="copilot.isActive.value && !showAudioRecorderEditor"
            :show-copilot-editor="copilot.showEditor.value"
            :is-generating-content="copilot.isGenerating.value"
            :generated-content="copilot.generatedContent.value"
            :is-popout="popOutReplyBox"
            :placeholder="$t('CONVERSATION.FOOTER.COPILOT_MSG_INPUT')"
            @focus="onFocus"
            @blur="onBlur"
            @clear-selection="clearEditorSelection"
            @close="copilot.showEditor.value = false"
            @content-ready="copilot.setContentReady"
            @send="copilot.sendFollowUp"
          />
          <WootMessageEditor
            v-else-if="!showAudioRecorderEditor && isComposerActive"
            ref="messageEditor"
            v-model="message"
            :conversation-id="conversationId"
            :editor-id="editorStateId"
            class="input popover-prosemirror-menu"
            :is-private="isOnPrivateNote"
            :placeholder="messagePlaceHolder"
            :aria-label="composerAriaLabel"
            :update-selection-with="updateEditorSelectionWith"
            :disabled="isEditorDisabled"
            :focus-on-mount="shouldFocusMessageEditorOnMount"
            enable-variables
            :variables="messageVariables"
            :signature="messageSignature"
            :allow-signature="!isAUnoapiChannel"
            :signature-preference-channel="signaturePreferenceChannel"
            :enable-group-mentions="canUseGroupMentions"
            :group-mention-contacts="groupMentionContacts"
            :channel-type="channelType"
            :medium="inbox.medium"
            @typing-off="onTypingOff"
            @typing-on="onTypingOn"
            @focus="onFocus"
            @blur="onBlur"
            @toggle-user-mention="toggleUserMention"
            @toggle-group-mention="toggleGroupMention"
            @toggle-canned-menu="toggleCannedMenu"
            @toggle-variables-menu="toggleVariablesMenu"
            @clear-selection="clearEditorSelection"
            @execute-copilot-action="executeCopilotAction"
          />
        </template>

        <QuotedEmailPreview
          v-if="shouldShowQuotedPreview && isDefaultEditorMode"
          :quoted-email-text="quotedEmailText"
          :preview-text="quotedEmailPreviewText"
          class="mb-2"
          @toggle="toggleQuotedReply"
        />

        <div
          v-if="(hasAttachedContacts || hasAttachments) && isDefaultEditorMode"
          class="bg-transparent py-0 mb-2"
          @paste="onPaste"
        >
          <AttachedContactsPreview
            v-if="hasAttachedContacts"
            :contacts="attachedContacts"
            @remove="removeAttachedContact"
          />
          <AttachmentPreview
            v-if="hasAttachments"
            class="mt-2"
            :attachments="attachedFiles"
            @remove-attachment="removeAttachment"
          />
        </div>
        <MessageSignatureMissingAlert
          v-if="
            isSignatureEnabledForInbox &&
            !isSignatureAvailable &&
            isDefaultEditorMode
          "
          class="mb-2"
        />
      </div>
    </Transition>

    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    >
      <CopilotReplyBottomPanel
        v-if="copilot.isActive.value"
        key="copilot-bottom-panel"
        :is-generating-content="copilot.isButtonDisabled.value"
        @submit="onSubmitCopilotReply"
        @cancel="copilot.reset"
      />
      <ReplyBottomPanel
        v-else-if="!useCompactMessageComposer"
        key="reply-bottom-panel"
        :allow-audio-view-once="
          isAUnoapiChannel && hasRecordedAudio && !isPrivate
        "
        :audio-view-once="recordedAudioViewOnce"
        :conversation-id="conversationId"
        :enable-multiple-file-upload="enableMultipleFileUpload"
        :enable-whats-app-templates="showWhatsappTemplates"
        :enable-content-templates="showContentTemplates"
        :inbox="inbox"
        :signature-preference-channel="signaturePreferenceChannel"
        :is-on-private-note="isOnPrivateNote"
        :is-recording-audio="isRecordingAudio"
        :is-send-disabled="isReplyButtonDisabled"
        :is-note="isPrivate"
        :is-editor-disabled="isEditorDisabled"
        :on-file-upload="onFileUpload"
        :on-send="onSendReply"
        :on-schedule="openScheduleModal"
        :conversation-type="conversationType"
        :recording-audio-duration-text="recordingAudioDurationText"
        :recording-audio-state="recordingAudioState"
        :send-button-text="replyButtonLabel"
        :show-audio-recorder="showAudioRecorder"
        :show-emoji-picker="showEmojiPicker"
        :show-file-upload="showFileUpload"
        :show-quoted-reply-toggle="shouldShowQuotedReplyToggle"
        :quoted-reply-enabled="quotedReplyPreference"
        :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
        :toggle-audio-recorder="toggleAudioRecorder"
        :toggle-emoji-picker="toggleEmojiPicker"
        :message="message"
        :portal-slug="connectedPortalSlug"
        :new-conversation-modal-active="newConversationModalActive"
        @update:audio-view-once="recordedAudioViewOnce = $event"
        @open-contact-picker="openContactAttachmentModal"
        @toggle-sticker-picker="showStickerPickerModal"
        @select-whatsapp-template="openWhatsappTemplateModal"
        @select-content-template="openContentTemplateModal"
        @toggle-insert-article="toggleInsertArticle"
        @toggle-quoted-reply="toggleQuotedReply"
      />
    </Transition>
    <WhatsappTemplates
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />
    <Modal
      v-model:show="showScheduleModal"
      :on-close="() => (showScheduleModal = false)"
    >
      <woot-modal-header
        header-title="Agendar mensagem"
        header-content="Escolha quando esta mensagem WhatsApp será enviada."
      />
      <form
        class="flex flex-col gap-5 p-8 pt-4 max-h-[78vh] overflow-y-auto"
        @submit.prevent="createScheduledMessage"
      >
        <label
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ scheduleCopy.dateTime }}
          <input
            v-model="scheduledAt"
            :min="toDatetimeLocal(new Date())"
            type="datetime-local"
            required
            class="w-full h-10 px-3 rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 outline-none focus:border-n-brand"
          />
        </label>
        <div class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12">
          {{ scheduleCopy.label }}
          <div class="relative">
            <button
              type="button"
              class="flex items-center justify-between w-full h-10 gap-2 px-3 text-left rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3"
              @click="showScheduleLabelDropdown = !showScheduleLabelDropdown"
            >
              <span class="flex items-center gap-2 min-w-0">
                <span
                  v-if="selectedScheduleLabel"
                  class="flex-shrink-0 rounded-sm size-2"
                  :style="{ backgroundColor: selectedScheduleLabel.color }"
                />
                <span class="truncate">
                  {{ selectedScheduleLabel?.title || 'Selecione uma etiqueta' }}
                </span>
              </span>
              <span class="i-lucide-chevron-down size-4 text-n-slate-10" />
            </button>
            <div
              v-if="showScheduleLabelDropdown"
              v-on-clickaway="() => (showScheduleLabelDropdown = false)"
              class="absolute z-50 w-full p-3 mt-1 rounded-lg border shadow-lg border-n-weak bg-n-solid-2"
            >
              <LabelDropdown
                :account-labels="accountLabels"
                :selected-labels="
                  selectedScheduleLabel ? [selectedScheduleLabel.title] : []
                "
                :allow-creation="isAdministrator"
                @add="selectScheduleLabel"
                @remove="scheduledLabelId = ''"
              />
            </div>
          </div>
        </div>
        <label
          v-if="isAdministrator"
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ scheduleCopy.agent }}
          <select
            v-model="scheduledSenderId"
            class="w-full h-10 px-3 rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option
              v-for="agent in scheduleAgents"
              :key="agent.id"
              :value="agent.id"
            >
              {{ agent.name }}
            </option>
          </select>
        </label>
        <label
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ scheduleCopy.reason }}
          <span class="font-normal text-n-slate-11">
            {{ scheduleCopy.optional }}
          </span>
          <textarea
            v-model="scheduledReason"
            rows="3"
            class="w-full px-3 py-2 rounded-lg border resize-none border-n-weak bg-n-alpha-2 text-n-slate-12 outline-none focus:border-n-brand"
          />
        </label>
        <ScheduledMessageSequenceEditor
          v-model="scheduledItems"
          rich-editor
          :audio-record-format="audioRecordFormat"
          :allowed-file-types="scheduleAllowedFileTypes"
          :conversation-id="conversationId"
          :channel-type="channelType"
          :medium="inbox.medium"
          :variables="messageVariables"
          :uploading="scheduleUploadCount > 0"
          @upload="onScheduleFileUpload"
        />
        <div class="flex justify-end gap-2 pt-1">
          <NextButton
            type="button"
            variant="ghost"
            color="slate"
            size="sm"
            label="Cancelar"
            @click="showScheduleModal = false"
          />
          <NextButton
            type="submit"
            color="blue"
            size="sm"
            label="Agendar mensagem"
            :disabled="!scheduledAt || !scheduledLabelId || !isScheduleValid"
          />
        </div>
      </form>
    </Modal>

    <StickerPickerDialog
      v-if="showStickerPicker"
      :show="showStickerPicker"
      :inbox-id="inboxId"
      @close="hideStickerPickerModal"
      @send="sendStickerMessage"
    />

    <ContentTemplates
      :inbox-id="inbox.id"
      :show="showContentTemplatesModal"
      @close="hideContentTemplatesModal"
      @on-send="onSendContentTemplateReply"
      @cancel="hideContentTemplatesModal"
    />

    <ContactAttachmentModal
      v-model:show="showContactAttachmentModal"
      :selected-contacts="attachedContacts"
      @close="hideContactAttachmentModal"
      @attach="setAttachedContacts"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
    <woot-confirm-modal
      ref="pixPaymentConfirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.PIX_PAYMENT.CONFIRM_TITLE')"
      :description="$t('CONVERSATION.REPLYBOX.PIX_PAYMENT.CONFIRM_DESCRIPTION')"
      :confirm-label="$t('CONVERSATION.REPLYBOX.PIX_PAYMENT.CONFIRM_LABEL')"
      :cancel-label="$t('CONVERSATION.REPLYBOX.PIX_PAYMENT.CANCEL_LABEL')"
      confirm-on-enter
    />
  </div>
</template>

<style lang="scss" scoped>
.send-button {
  @apply mb-0;
}

.reply-box {
  @apply relative mb-2 mx-2 border border-n-weak rounded-xl bg-n-solid-1;

  &.is-private {
    @apply bg-n-solid-amber dark:border-n-amber-3/10 border-n-amber-12/5;
  }

  &.is-compact {
    @apply border-0 bg-transparent;

    .reply-box__top {
      @apply px-0 mt-0;
    }
  }
}

.send-button {
  @apply mb-0;
}

.reply-box__top {
  @apply relative py-0 px-3 -mt-px;
}

.emoji-dialog {
  @apply top-[unset] -bottom-10 ltr:-left-80 ltr:right-[unset] rtl:left-[unset] rtl:-right-80;

  &::before {
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));
    @apply ltr:-right-4 bottom-2 rtl:-left-4 ltr:rotate-[270deg] rtl:rotate-[90deg];
  }
}

.emoji-dialog--expanded {
  @apply left-[unset] bottom-0 absolute z-[100];

  &::before {
    transform: rotate(0deg);
    @apply ltr:left-1 rtl:right-1 -bottom-2;
  }
}
</style>
